import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

const CANVAS = { width: 1920, height: 1080 } as const;

interface SlideFrameProps {
  children: ReactNode;
  index?: number;
  label?: string;
  className?: string;
  /**
   * Thumbnail mode: scale the slide to fit the iframe width and center it vertically (letterboxed)
   * using pure-CSS viewport units instead of a ResizeObserver. Required wherever the frame renders
   * without JS — the thumbnail preview iframe is sandboxed `allow-same-origin` (no `allow-scripts`),
   * so the observer never fires and the canvas would otherwise stay at `scale(1)` and show only its
   * cropped top-left corner. Fit-to-width keeps the whole slide visible; on non-16:9 cards (e.g.
   * square ones) the leftover height is split evenly above and below as letterbox bars.
   */
  staticScale?: boolean;
}

/**
 * Fixed 1920×1080 logical canvas scaled to fit the parent container width.
 * Outer fills 100% of its parent's content box (via aspect-ratio for height); a
 * ResizeObserver reads the rendered width to drive the inner canvas's CSS
 * transform-scale. In `staticScale` mode the frame instead fills the iframe viewport and the canvas
 * is fit-to-width scaled with viewport units and centered (no JS). Canvas uses a single-cell grid
 * to stretch the agent's `flex flex-col` section to fill 1920×1080.
 */
export function SlideFrame({ children, index, label, className, staticScale = false }: SlideFrameProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const [renderedWidth, setRenderedWidth] = useState(0);

  useEffect(() => {
    if (staticScale) return;
    const el = outerRef.current;
    if (!el) return;
    const compute = () => setRenderedWidth(el.clientWidth);
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [staticScale]);

  const scale = renderedWidth > 0 ? renderedWidth / CANVAS.width : 1;

  return (
    <div
      ref={outerRef}
      data-slide-frame
      data-slide-frame-index={index}
      data-screen-label={label ?? (index !== undefined ? `${String(index + 1).padStart(2, '0')} Slide` : undefined)}
      className={cn(
        staticScale
          ? 'fixed inset-0 flex items-center justify-center overflow-hidden'
          : 'relative w-full overflow-hidden',
        className,
      )}
      style={staticScale ? undefined : { aspectRatio: `${CANVAS.width} / ${CANVAS.height}` }}
    >
      <div
        data-slide-frame-canvas
        style={{
          width: CANVAS.width,
          height: CANVAS.height,
          transform: staticScale ? `scale(calc(100vw / ${CANVAS.width}px))` : `scale(${scale})`,
          transformOrigin: staticScale ? 'center center' : 'top left',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
        }}
        className={staticScale ? 'shrink-0' : 'absolute left-0 top-0'}
      >
        {children}
      </div>
    </div>
  );
}
